import { Circle, TrendingDown, TrendingUp } from "lucide-react";
import type { CSSProperties } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components";
import { TableErrorState } from "../../../../components/table";
import { Row } from "../../../../components/row";
import type { AreaAnalysisModel } from "../../types/AreaAnalysis";
import { TableRowsSkeleton } from "../region/region-skeletons";

const ENGAGEMENT_HIGH_THRESHOLD = 5;
const ENGAGEMENT_MODERATE_THRESHOLD = 2;

const engagementStatusColor = (engagement?: number) => {
  if (engagement == null) return "#9CA3AF";
  if (engagement > ENGAGEMENT_HIGH_THRESHOLD) return "#00C853";
  if (engagement >= ENGAGEMENT_MODERATE_THRESHOLD) return "#FF9800";
  return "red";
};

type AreaAnalysisTableProps = {
  data: AreaAnalysisModel[] | null;
  maxHeight?: string;
  maxWidth?: string;
  scale?: number;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
};

const AreaAnalysisTable = ({
  data: areaAnalysis,
  maxHeight = "830px",
  maxWidth = undefined,
  scale = 1,
  isLoading = false,
  error,
  onRetry,
}: AreaAnalysisTableProps) => {
  const rows = (areaAnalysis || [])
    .slice()
    .sort((a: AreaAnalysisModel, b: AreaAnalysisModel) => {
      const nameA = (a?.area_name || "").toLowerCase();
      const nameB = (b?.area_name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((data: AreaAnalysisModel) => {
      const growth = data?.growth;
      return (
        <TableRow key={data?.organization_population_id}>
          <TableCell className=" pr-1 max-w-[100px]">
            {" "}
            {/* keep max-width = 100px to prevent breaking PDF report */}
            {data?.area_name}
          </TableCell>
          <TableCell className="text-center px-1">
            {data?.population?.toLocaleString("en-US")}
          </TableCell>
          <TableCell className="text-center px-1">
            {data?.civic_points_user?.toLocaleString("en-US")}
          </TableCell>
          <TableCell className="text-center px-1">
            {data?.target_audience?.toLocaleString("en-US")}
          </TableCell>
          <TableCell>
            <div className="flex justify-center items-center">
              <span className="w-[50px] text-right">{data?.adoption_rate?.toFixed(2)}%</span>
              <Circle
                className="ml-2"
                stroke="none"
                size={12}
                fill={
                  data?.adoption_rate > 70
                    ? "#00C853"
                    : data?.adoption_rate >= 50
                      ? "#FF9800"
                      : "red"
                }
              />
            </div>
          </TableCell>
          <TableCell className="px-1">
            <div className="flex justify-center items-center">
              <span className="w-[50px] text-right">{data?.engagement?.toFixed(2)}%</span>
              <Circle
                className="ml-2"
                stroke="none"
                size={12}
                fill={engagementStatusColor(data?.engagement)}
              />
            </div>
          </TableCell>
          <TableCell className="px-1">
            <div className="flex justify-center items-center">
              {growth == null ? (
                <span>-</span>
              ) : (
                <>
                  {growth > 0 && (
                    <TrendingUp
                      className="mr-2"
                      stroke="none"
                      size={25}
                      fill={"#00C853"}
                    />
                  )}
                  {growth < 0 && (
                    <TrendingDown
                      className="mr-2"
                      stroke="none"
                      size={25}
                      fill={"#C10007"}
                    />
                  )}
                  {growth === 0 && <span className="w-[25px] mr-2"></span>}
                  <span>{growth > 0 ? `+${growth.toFixed(1)}%` : `${growth.toFixed(1)}%`}</span>
                </>
              )}
            </div>
          </TableCell>
          {/* <TableCell className="text-center">
                              {data?.adoption_rate >= 70 &&
                                  <span className="border border-[#00C853] text-[#00C853] bg-[#DBFCE7] py-1 px-3 rounded-2xl">
                                      {data?.priority}
                                  </span>
                              }
                              {data?.adoption_rate >= 50 && data?.adoption_rate < 70 &&
                                  <span className="border border-[#FF9800] text-[#FF9800] bg-[#FFF3E0] py-1 px-3 rounded-2xl">
                                      {data?.priority}
                                  </span>
                              }
                              {data?.adoption_rate < 50 &&
                                  <span className="border border-red-500 text-red-500 bg-red-100 py-1 px-3 rounded-2xl">
                                      {data?.priority}
                                  </span>
                              }
                          </TableCell> */}
        </TableRow>
      );
    });

  return (
    <div
      className={`w-full mt-5 border border-gray-200 dark:border-gray-700 rounded-lg`}
      style={
        {
          maxWidth,
          "--hm-area-analysis-max-height": maxHeight,
        } as CSSProperties
      }
    >
      <div className="px-6 py-6">
        <Row className="mb-2">
          <span className="text-xl block font-semibold text-[#364153] dark:text-gray-100">
            Participation Gaps & Opportunities
          </span>
        </Row>
        <Row>
          <span className="block text-[#4A5565] dark:text-gray-400">
            Identify neighborhoods with low adoption, participation, and resident engagement to
            guide targeted outreach strategies.
          </span>
        </Row>
      </div>
      <Row
        style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}
        className="mt-5 relative"
      >
        <Table
          containerClassName="max-h-[var(--hm-area-analysis-max-height)] overflow-y-auto scrollbar-hide"
          className="min-w-max"
        >
          <TableHeader className="bg-[#F7F8FA] dark:bg-gray-700 sticky top-[-5px] z-10">
            <TableRow>
              <TableHead className="text-[#364153] dark:text-gray-100 font-bold pr-1">
                Area Name
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold px-1">
                Population
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold px-1">
                CivicPoints Users
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold px-1">
                Target Audience
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold px-1">
                Adoption Rate
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold px-1">
                Engagement
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-bold px-1">
                Growth
              </TableHead>
              {/* <TableHead className="text-center">Priority</TableHead> */}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRowsSkeleton colSpan={7} />
            ) : error ? (
              <TableErrorState
                colSpan={7}
                error={error}
                onRetry={onRetry}
              />
            ) : (
              rows
            )}
          </TableBody>
        </Table>
      </Row>
    </div>
  );
};

export default AreaAnalysisTable;
